export const MENSAJE_VALOR_REQUERIDO = "Ingresa un valor.";
export const MENSAJE_VALOR_NO_NUMERICO = "Ingresa un valor numérico (usa punto o coma para decimales).";
export const MENSAJE_VALOR_NO_POSITIVO = "El valor debe ser mayor a 0.";

const PATRON_NUMERO = /^[+-]?(\d+([.,]\d+)?|[.,]\d+)$/;

/**
 * Interpreta el texto crudo de un campo numérico. Acepta coma o punto decimal.
 * @param {string|number|null|undefined} valor
 * @returns {{estado: "vacio"|"numero"|"noNumerico", valor: number|null}}
 */
export function parseValorMedicion(valor) {
	if (valor == null) {
		return { estado: "vacio", valor: null };
	}
	if (typeof valor === "number") {
		return Number.isFinite(valor)
			? { estado: "numero", valor }
			: { estado: "noNumerico", valor: null };
	}
	if (typeof valor !== "string") {
		return { estado: "noNumerico", valor: null };
	}
	const texto = valor.trim();
	if (texto === "") {
		return { estado: "vacio", valor: null };
	}
	// El patrón rechaza notación científica, hexadecimal y "90." que Number() aceptaría
	if (!PATRON_NUMERO.test(texto)) {
		return { estado: "noNumerico", valor: null };
	}
	const numero = Number(texto.replace(",", "."));
	if (!Number.isFinite(numero)) {
		return { estado: "noNumerico", valor: null };
	}
	return { estado: "numero", valor: numero };
}

/**
 * Valida un campo que debe ser un número mayor a 0.
 * @returns {{error: string|null, valor: number|null}}
 */
export function validarCampoPositivo(valor) {
	const { estado, valor: numero } = parseValorMedicion(valor);
	if (estado === "vacio") {
		return { error: MENSAJE_VALOR_REQUERIDO, valor: null };
	}
	if (estado === "noNumerico") {
		return { error: MENSAJE_VALOR_NO_NUMERICO, valor: null };
	}
	if (numero <= 0) {
		return { error: MENSAJE_VALOR_NO_POSITIVO, valor: null };
	}
	return { error: null, valor: numero };
}

/**
 * Validador del panel de glucometría (contrato del patrón de panel).
 * @param {{glucometria?: string}} valores textos crudos del formulario
 * @returns {{valido: boolean, errores: Object, datos: Object}}
 */
export function validarGlucometria(valores) {
	const { error, valor } = validarCampoPositivo((valores || {}).glucometria);
	if (error) {
		return { valido: false, errores: { glucometria: error }, datos: {} };
	}
	return { valido: true, errores: {}, datos: { glucometria: valor } };
}

export const MENSAJE_PORCENTAJE_NO_VALIDO =
	"El porcentaje calculado (PEF / PRED) debe ser mayor a 0. Revisa los valores.";

/**
 * Porcentaje de espirometría: Math.round(pef / pred * 100).
 * @param {string|number|null|undefined} pef  texto crudo o número
 * @param {string|number|null|undefined} pred texto crudo o número
 * @returns {number|null} entero, o null si no es calculable
 */
export function calcularPorcentajeEspirometria(pef, pred) {
	const a = parseValorMedicion(pef);
	const b = parseValorMedicion(pred);
	if (a.estado !== "numero" || b.estado !== "numero" || a.valor <= 0 || b.valor <= 0) {
		return null;
	}
	const porcentaje = Math.round((a.valor / b.valor) * 100);
	return Number.isFinite(porcentaje) ? porcentaje : null;
}

/**
 * Validador del panel de espirometría (contrato del patrón de panel).
 * El porcentaje se calcula aquí: lo enviado es exactamente lo mostrado.
 * @param {{pef?: string, pred?: string}} valores textos crudos del formulario
 * @returns {{valido: boolean, errores: Object, datos: Object}}
 */
export function validarEspirometria(valores) {
	const v = valores || {};
	const pef = validarCampoPositivo(v.pef);
	const pred = validarCampoPositivo(v.pred);
	const errores = {};
	if (pef.error) {
		errores.pef = pef.error;
	}
	if (pred.error) {
		errores.pred = pred.error;
	}
	if (pef.error || pred.error) {
		return { valido: false, errores, datos: {} };
	}
	const porcentaje = calcularPorcentajeEspirometria(pef.valor, pred.valor);
	if (porcentaje == null || porcentaje <= 0) {
		return {
			valido: false,
			errores: { porcentaje: MENSAJE_PORCENTAJE_NO_VALIDO },
			datos: {},
		};
	}
	return {
		valido: true,
		errores: {},
		datos: { pef: pef.valor, pred: pred.valor, porcentaje },
	};
}

/**
 * Valida un campo numérico dentro de un rango inclusivo. Los mensajes incluyen
 * el rango permitido. Lo reutilizará la feature 13 (perímetro 40-200 cm).
 * @param {string|number|null|undefined} valor texto crudo o número
 * @param {{min: number, max: number, unidad: string}} rango
 * @returns {{error: string|null, valor: number|null}}
 */
export function validarCampoEnRango(valor, rango) {
	const { estado, valor: numero } = parseValorMedicion(valor);
	const texto = `${rango.min} y ${rango.max} ${rango.unidad}`;
	if (estado === "vacio") {
		return { error: `Ingresa un valor entre ${texto}.`, valor: null };
	}
	if (estado === "noNumerico") {
		return {
			error: `Ingresa un valor numérico entre ${texto} (usa punto o coma para decimales).`,
			valor: null,
		};
	}
	if (numero < rango.min || numero > rango.max) {
		return { error: `El valor debe estar entre ${texto}.`, valor: null };
	}
	return { error: null, valor: numero };
}

// Rangos medibles del backend (LipidogramaService, enum ComponenteLipidograma), inclusivos
export const RANGOS_LIPIDOGRAMA = Object.freeze({
	colesterol: Object.freeze({ min: 50, max: 1000, unidad: "mg/dL" }),
	ldl: Object.freeze({ min: 10, max: 1000, unidad: "mg/dL" }),
	hdl: Object.freeze({ min: 5, max: 200, unidad: "mg/dL" }),
	trigliceridos: Object.freeze({ min: 20, max: 5000, unidad: "mg/dL" }),
});

/**
 * Validador del panel de lipidograma (contrato del patrón de panel).
 * Cada campo se evalúa por separado contra su rango medible.
 * @param {{colesterol?: string, ldl?: string, hdl?: string, trigliceridos?: string}} valores textos crudos
 * @returns {{valido: boolean, errores: Object, datos: Object}}
 */
export function validarLipidograma(valores) {
	const v = valores || {};
	const errores = {};
	const datos = {};
	Object.keys(RANGOS_LIPIDOGRAMA).forEach((clave) => {
		const { error, valor } = validarCampoEnRango(v[clave], RANGOS_LIPIDOGRAMA[clave]);
		if (error) {
			errores[clave] = error;
		} else {
			datos[clave] = valor;
		}
	});
	return Object.keys(errores).length
		? { valido: false, errores, datos: {} }
		: { valido: true, errores: {}, datos };
}

/**
 * Texto de ayuda con el rango que el sistema acepta (no es referencia clínica).
 * Genérico: lo usa el lipidograma y lo reutilizará la feature 13 (40-200 cm).
 * @param {{min: number, max: number, unidad: string}} rango
 * @returns {string} p. ej. "Rango permitido: 50 - 1000 mg/dL"
 */
export function textoRangoPermitido(rango) {
	return `Rango permitido: ${rango.min} - ${rango.max} ${rango.unidad}`;
}

// Rango medible del backend (PerimetroAbdominalService), inclusivo
export const RANGO_PERIMETRO_ABDOMINAL = Object.freeze({ min: 40, max: 200, unidad: "cm" });

// Paso del slider de perímetro abdominal (cm)
export const PASO_PERIMETRO_ABDOMINAL = 0.5;

/**
 * Validador del panel de perímetro abdominal (contrato del patrón de panel).
 * @param {{perimetro?: string|number}} valores textos crudos del formulario
 * @returns {{valido: boolean, errores: Object, datos: Object}}
 */
export function validarPerimetroAbdominal(valores) {
	const { error, valor } = validarCampoEnRango((valores || {}).perimetro, RANGO_PERIMETRO_ABDOMINAL);
	if (error) {
		return { valido: false, errores: { perimetro: error }, datos: {} };
	}
	return { valido: true, errores: {}, datos: { perimetro: valor } };
}

/**
 * Posición del slider para el texto del campo: el número acotado al rango,
 * o el mínimo si el texto está vacío o no es numérico.
 * @param {string|number|null|undefined} texto
 * @returns {number}
 */
export function valorSliderPerimetro(texto) {
	const { estado, valor } = parseValorMedicion(texto);
	const { min, max } = RANGO_PERIMETRO_ABDOMINAL;
	if (estado !== "numero") {
		return min;
	}
	return Math.min(max, Math.max(min, valor));
}
